import { Link, useNavigate } from 'react-router-dom';
import Button from '../../ui/Button';
import {
  WorkspacePageError,
  WorkspacePageLoader,
  WorkspaceShell,
} from '../../ui/Workspace';
import RevenueChart from './RevenueChart';
import RecentEnrollments from './RecentEnrollments';
import { useAdminCoursesOverview } from '../../../hooks/useLmsData';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useAdminCoursesOverview();

  const stats = data?.stats || {
    totalStudents: 0,
    activeCourses: 0,
    totalRevenue: 0,
    pendingPayments: 0,
  };
  const courseList = data?.courseList || [];
  const students = data?.students || {};
  const payments = data?.payments || [];
  const batchRows = courseList.flatMap((course) =>
    (course.seatSummary || []).map((batch) => ({
      ...batch,
      id: `${course.id}-${batch.batchId || batch.batchIndex}`,
      courseName: course.name || course.title || 'Course',
      batchName: batch.batchId || `Batch ${batch.batchIndex + 1}`,
      branchLabel: batch.branchName || batch.branch || 'Branch not set',
    })),
  );
  const studentRows = Object.entries(students)
    .map(([id, student]) => ({
      id,
      name:
        `${student?.personalDetails?.firstName || student?.firstName || ''} ${
          student?.personalDetails?.lastName || student?.lastName || ''
        }`.trim() ||
        student?.email ||
        'Student',
      email: student?.personalDetails?.email || student?.email || '',
      phone: student?.personalDetails?.phone || student?.phone || '',
      branch:
        student?.branchName ||
        student?.personalDetails?.branchName ||
        student?.branch ||
        '-',
      status: student?.status || 'active',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const latestPayments = [...payments]
    .sort(
      (a, b) =>
        new Date(b.paymentDate || b.createdAt || 0) -
        new Date(a.paymentDate || a.createdAt || 0),
    )
    .slice(0, 8);
  const coursesMissingLessons = courseList.filter(
    (course) => !Array.isArray(course.lessons) || course.lessons.length === 0,
  ).length;
  const coursesMissingInstructors = courseList.filter(
    (course) => !course.instructors || course.instructors.length === 0,
  ).length;

  if (loading) return <WorkspacePageLoader />;
  if (error) return <WorkspacePageError>{error.message}</WorkspacePageError>;

  return (
    <WorkspaceShell
      title="Dashboard"
      description="A compact operations overview for courses, batches, students, and payments."
      actions={
        <>
          <Button onClick={() => navigate('/reception/kiosk')}>Open Kiosk</Button>
          <Button variant="outline" onClick={() => navigate('/add-student')}>
            Add Student
          </Button>
          <Button variant="outline" onClick={() => navigate('/courses/add')}>
            Add Course
          </Button>
        </>
      }
    >
      <StatRow
        items={[
          { label: 'Students', value: stats.totalStudents },
          { label: 'Active courses', value: stats.activeCourses },
          {
            label: 'Revenue',
            value: `Rs. ${Number(stats.totalRevenue || 0).toLocaleString()}`,
          },
          {
            label: 'Pending payments',
            value: stats.pendingPayments,
            tone: stats.pendingPayments ? 'warning' : 'muted',
          },
        ]}
      />

      <KioskLaunchCard />

      <div>
        <SectionHeader title="Quick actions" hint="Jump straight into daily work" />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <QuickLink
            to="/enrollments"
            label="Enrollments"
            badge={data?.recentEnrollments?.length || 0}
          />
          <QuickLink
            to="/payments"
            label="Payments"
            badge={stats.pendingPayments || 0}
            badgeTone={stats.pendingPayments ? 'warning' : 'muted'}
          />
          <QuickLink to="/admin/batches" label="Batch Ledger" />
          <QuickLink to="/students" label="Students" />
          <QuickLink to="/instructors" label="Staff" />
          <QuickLink
            to="/lesson-planner"
            label="Lessons"
            badge={coursesMissingLessons || 0}
            badgeTone={coursesMissingLessons ? 'warning' : 'muted'}
          />
          <QuickLink to="/admin/verifications" label="Verifications" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel
          title="Courses"
          description={`${courseList.length} catalog records`}
          action={<TableLink to="/courses">Manage</TableLink>}
        >
          <DashboardTable
            columns={['Course', 'Code', 'Batches', 'Students', 'Status']}
            rows={courseList.slice(0, 8).map((course) => [
              course.name || course.title || 'Untitled course',
              course.courseCode || '-',
              course.seatSummary?.length || 0,
              course.activeEnrollmentCount || 0,
              <StatusText key="status" value={course.status || 'draft'} />,
            ])}
            empty="No courses yet."
          />
        </Panel>

        <Panel
          title="Batches"
          description={`${batchRows.length} branch/batch rows`}
          action={<TableLink to="/courses">Manage</TableLink>}
        >
          <DashboardTable
            columns={['Batch', 'Course', 'Branch', 'Seats', 'Start']}
            rows={batchRows.slice(0, 8).map((batch) => [
              batch.batchName,
              batch.courseName,
              batch.branchLabel,
              `${batch.enrolledCount || 0}/${batch.maxStudents || 0}`,
              formatDate(batch.startDate),
            ])}
            empty="No batches configured."
          />
        </Panel>

        <Panel
          title="Students"
          description={`${studentRows.length} student records`}
          action={<TableLink to="/students">Manage</TableLink>}
        >
          <DashboardTable
            columns={['Student', 'Email', 'Phone', 'Branch', 'Status']}
            rows={studentRows.slice(0, 8).map((student) => [
              student.name,
              student.email || '-',
              student.phone || '-',
              student.branch || '-',
              <StatusText key="status" value={student.status} />,
            ])}
            empty="No students yet."
          />
        </Panel>

        <Panel
          title="Payments"
          description={`${payments.length} payment records`}
          action={<TableLink to="/payments">Manage</TableLink>}
        >
          <DashboardTable
            columns={['Student', 'Batch', 'Amount', 'Date', 'Status']}
            rows={latestPayments.map((payment) => [
              payment.studentName || 'Student',
              payment.batchName || '-',
              `Rs. ${Number(payment.amount || 0).toLocaleString()}`,
              formatDate(payment.paymentDate || payment.createdAt),
              <StatusText key="status" value={payment.status || 'pending'} />,
            ])}
            empty="No payments yet."
          />
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Panel
          title="Revenue"
          description="Verified payments trend"
          action={<TableLink to="/payments">View payments</TableLink>}
        >
          <RevenueChart revenueData={data?.monthlyRevenue || []} />
        </Panel>

        <Panel
          title="Recent enrollments"
          description={`${data?.recentEnrollments?.length || 0} recent changes`}
          action={<TableLink to="/enrollments">Open queue</TableLink>}
        >
          <RecentEnrollments enrollments={data?.recentEnrollments || []} />
        </Panel>
      </div>

      {coursesMissingInstructors ? (
        <AttentionBar
          label={`${coursesMissingInstructors} courses still need teaching staff assigned.`}
          to="/instructors"
          actionLabel="Assign staff"
        />
      ) : null}
    </WorkspaceShell>
  );
};

const StatRow = ({ items }) => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
    {items.map((item) => (
      <div
        key={item.label}
        className="rounded-lg border border-stone-200 bg-white px-4 py-3"
      >
        <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {item.label}
        </p>
        <p
          className={`mt-1 text-2xl font-semibold tracking-tight ${
            item.tone === 'warning' ? 'text-amber-700' : 'text-stone-950'
          }`}
        >
          {item.value}
        </p>
      </div>
    ))}
  </div>
);

const KioskLaunchCard = () => (
  <Link
    to="/reception/kiosk"
    className="group flex items-center justify-between gap-4 rounded-lg border border-pink-200 bg-white px-5 py-4 transition hover:border-[#d61f74]"
  >
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#b91c66]">
        Reception
      </p>
      <p className="mt-0.5 text-base font-semibold text-stone-950">
        Open Reception Kiosk
      </p>
      <p className="mt-0.5 text-sm text-stone-600">
        Scan QR codes, mark attendance, quick-register students, and take payments.
      </p>
    </div>
    <span className="hidden text-sm font-semibold text-[#b91c66] transition group-hover:translate-x-0.5 sm:inline">
      Launch
    </span>
  </Link>
);

const SectionHeader = ({ title, hint }) => (
  <div className="mb-3 flex items-baseline justify-between gap-3">
    <h2 className="text-base font-semibold tracking-tight text-stone-950">
      {title}
    </h2>
    {hint ? <p className="text-xs text-stone-500">{hint}</p> : null}
  </div>
);

const QuickLink = ({ to, label, badge, badgeTone = 'muted' }) => (
  <Link
    to={to}
    className="group flex min-h-[64px] items-center justify-between rounded-lg border border-stone-200 bg-white px-3 py-2 transition hover:border-stone-300 hover:bg-stone-50"
  >
    <p className="text-sm font-semibold tracking-tight text-stone-950">{label}</p>
    {badge ? (
      <span
        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
          badgeTone === 'warning'
            ? 'bg-amber-100 text-amber-800'
            : 'bg-stone-100 text-stone-700'
        }`}
      >
        {badge}
      </span>
    ) : null}
  </Link>
);

const Panel = ({ title, description, action, children }) => (
  <section className="rounded-lg border border-stone-200 bg-white p-4">
    <header className="mb-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-base font-semibold tracking-tight text-stone-950">
          {title}
        </h2>
        {description ? (
          <p className="mt-0.5 text-xs text-stone-500">{description}</p>
        ) : null}
      </div>
      {action}
    </header>
    {children}
  </section>
);

const TableLink = ({ to, children }) => (
  <Link
    to={to}
    className="text-xs font-semibold text-[#b91c66] hover:text-[#9d174d]"
  >
    {children}
  </Link>
);

const DashboardTable = ({ columns, rows, empty }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-stone-200 text-sm">
      <thead className="bg-stone-50">
        <tr>
          {columns.map((column) => (
            <th
              key={column}
              className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-stone-500"
            >
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-stone-100 bg-white">
        {rows.length ? (
          rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="hover:bg-stone-50">
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="whitespace-nowrap px-3 py-2 text-stone-800"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={columns.length} className="px-3 py-8 text-center text-stone-500">
              {empty}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const StatusText = ({ value }) => (
  <span className="inline-flex rounded-full bg-stone-100 px-2 py-0.5 text-xs font-semibold capitalize text-stone-700">
    {String(value || '').replace(/_/g, ' ')}
  </span>
);

const formatDate = (value) => {
  if (!value) return '-';
  if (value.year && value.month && value.date) {
    return new Date(value.year, value.month - 1, value.date).toLocaleDateString();
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const AttentionBar = ({ label, to, actionLabel }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
    <p className="text-sm text-amber-900">{label}</p>
    <Link
      to={to}
      className="text-xs font-semibold text-amber-900 underline-offset-2 hover:underline"
    >
      {actionLabel}
    </Link>
  </div>
);

export default AdminDashboard;
